import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { Skill, buildSkillSlug } from "@/v2/entities/skill/Skill.model";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";

export type BrowserSkillFormValues = {
  name: string;
  description: string;
  startUrl: string;
  profileName?: string | null;
  slug?: string;
};

interface UseBrowserSkillFormOptions {
  mode: "create" | "edit";
  initialValues?: BrowserSkillFormValues;
  existingSkills?: Skill[];
  skillId?: string;
}

const httpUrlSchema = z
  .string()
  .trim()
  .url()
  .refine((value) => value.startsWith("http://") || value.startsWith("https://"));

export function useBrowserSkillForm({
  mode,
  initialValues,
  existingSkills = [],
  skillId,
}: UseBrowserSkillFormOptions) {
  const defaults = useMemo(
    () => ({
      name: initialValues?.name ?? "",
      description: initialValues?.description ?? "",
      startUrl: initialValues?.startUrl ?? "",
      slug: initialValues?.slug,
    }),
    [initialValues]
  );

  const [name, setName] = useState(defaults.name);
  const [description, setDescription] = useState(defaults.description);
  const [startUrl, setStartUrl] = useState(defaults.startUrl);
  const [urlValid, setUrlValid] = useState(() =>
    httpUrlSchema.safeParse(defaults.startUrl).success
  );
  const [showErrors, setShowErrors] = useState(false);
  const { workspace } = useWorkspace();

  const slug = useMemo(
    () => buildSkillSlug(name || defaults.slug || ""),
    [name, defaults.slug]
  );

  const isDuplicateSlug = useMemo(
    () =>
      existingSkills.some(
        (skill) => skill.slug === slug && skill.id !== skillId
      ),
    [existingSkills, slug, skillId]
  );

  const isValid = useMemo(
    () =>
      Boolean(name.trim()) &&
      Boolean(description.trim()) &&
      urlValid &&
      !isDuplicateSlug,
    [name, description, urlValid, isDuplicateSlug]
  );

  const handleNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const handleDescriptionChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setDescription(event.target.value);
    },
    []
  );

  const handleStartUrlChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setStartUrl(value);
    setUrlValid(httpUrlSchema.safeParse(value).success);
  }, []);

  // Always compute and use profile name
  const profileName = useMemo(() => {
    const tenant = workspace || "unknown";
    return `${tenant}/${slug}`;
  }, [slug, workspace]);

  const reset = useCallback(() => {
    setName(defaults.name);
    setDescription(defaults.description);
    setStartUrl(defaults.startUrl);
    setUrlValid(httpUrlSchema.safeParse(defaults.startUrl).success);
    setShowErrors(false);
  }, [defaults]);

  const getValues = useCallback((): BrowserSkillFormValues => {
    return {
      name: name.trim(),
      description: description.trim(),
      startUrl: startUrl.trim(),
      profileName,
      slug,
    };
  }, [
    name,
    description,
    startUrl,
    profileName,
    slug,
  ]);

  return {
    // Values
    name,
    description,
    startUrl,
    profileName,
    slug,

    // Validation
    urlValid,
    isDuplicateSlug,
    isValid,
    showErrors,

    // Setters
    setName,
    setDescription,
    setStartUrl,
    setShowErrors,

    // Handlers
    handleNameChange,
    handleDescriptionChange,
    handleStartUrlChange,

    // Utility
    reset,
    getValues,
  };
}
